import axios from "axios";
import type {DashboardStats} from "@/models/dtos/dashboardStats";
import type {IdentityDto} from "@/models/dtos/identityDto";
import type {OnboardingDataDto} from "@/models/dtos/onboardingDataDto";
import setupTokenInterceptors from "@/clients/tokenInterceptors"
import type {EnergyUsageDto} from "@/models/dtos/energyUsageDto";
import type {EnergyCommunityDto} from "@/models/dtos/energyCommunityDto";
import type {MeteringPointDto} from "@/models/dtos/meteringPointDto";
import type {CommunityDashboardStatsDto} from "@/models/dtos/CommunityDashboardStatsDto";
import type {EdaConversationDto} from "@/models/dtos/edaConversationDto";
import type {SystemEventDto, SystemEventPageDto, SystemEventQueryDto} from "@/models/dtos/systemEventDto";
import type {EdaMessageDto} from "@/models/dtos/edaMessageDto";
import type {EnergyDataAvailabilityDto} from "@/models/dtos/energyDataAvailabilityDto";
import type {EnergyCommunityDataSummaryDto} from "@/models/dtos/energyCommunityDataSummaryDto";
import type {EnergyDataRequestOverviewDto, EnergyDataRequestOverviewQuery} from "@/models/dtos/energyDataRequestOverviewDto";
import type {NotificationPossibleDto} from "@/models/dtos/notificationPossibleDto";
import type {PublicOnboardingRequestDto} from "@/models/dtos/publicOnboardingRequestDto";
import type {OnboardingConfigDto} from "@/models/dtos/onboardingConfigDto";
import type {SubmissionConfirmationResponse} from "@/models/dtos/submissionConfirmationDto";
import type {BankDataDto, BankDataSubmissionResponse} from "@/models/dtos/bankDataDto";
import type {AnalyticsEventDto} from "@/models/dtos/onboardingEventDto";
import type {ProcessOnboardingRequestDto, ProcessOnboardingResponseDto} from "@/models/dtos/processOnboardingDto";
import type {RequestMissingDataDto} from "@/models/dtos/requestMissingDataDto";
import type {CommunityWithdrawalRequestDto, ParticipationFactorChangeRequestDto, PontonProcessResponseDto} from "@/models/dtos/pontonProcess";
import type {AccountLoginDataDto, AccountLoginUpdateResultDto} from "@/models/dtos/accountLoginDataDto";
import type {CommunityRecommendationResponseDto, RecommendationResponseDto} from "@/models/dtos/recommendationResponseDto";
import type {BillabilityDto, BillabilityRequestDto} from "@/models/dtos/billingDto";
import type {TriggerBillingDto, QueueInvoiceDocumentsRequestDto} from "@/models/dtos/billingDto";
import type {EnergyTimeSeriesDto, TimeseriesGranularity} from "@/models/dtos/energyTimeSeriesDto";
import type {EnergyGraphRequestDto, EnergyGraphResponseDto} from "@/models/dtos/energyGraphDto";
import type {InvoiceRunBreakdownDto} from "@/models/dtos/invoiceRunBreakdownDto";
import type {AdminInvoiceRunDto, AdminInvoiceRunListResponseDto} from "@/models/dtos/adminInvoiceRunDto";
import type {AdminInvoiceRunOverviewDto, InvoiceRunComparisonDto, InvoiceRunTodoDto, InvoiceRunTodoListResponseDto} from "@/models/dtos/adminBillingDto";
import type {PagedResult} from "@/models/dtos/pagedResult";
import type {CommunityMeteringPointDto, CommunityMeteringPointQuery} from "@/models/dtos/communityMeteringPointDto";
import type {FileListQuery, FileListResponseDto, FileDto, FileUploadPayload, FileUpdatePayload, BulkFileDownloadRequest} from "@/models/dtos/fileDto";
import type {CommunityTariffsResponseDto, CreateFixedTariffDto, UpdateFixedTariffDto, FixedTariffDto, CreateFlatRateTariffDto, UpdateFlatRateTariffDto, FlatRateTariffDto} from "@/models/dtos/tariffDto";
import type {UserTransactionDto, CreateUserTransactionDto, UpdateUserTransactionDto, UserSearchResultDto} from "@/models/dtos/userTransactionDto";
import type {OnboardingFormItemDto, OnboardingFormItemWriteDto} from "@/models/dtos/onboardingFormItemDto";
import type {WeeklyRecommendationRunDto, WeeklyRecommendationRunState} from "@/models/dtos/weeklyRecommendationRunDto";
import type {BrandOptionDto} from "@/models/dtos/brandOptionDto";
import type {CreateAdminUserRequestDto} from "@/models/dtos/createAdminUserRequestDto";
import type {BrandCommunityDesignDto, BrandTemplateDto, CreateBrandWithDesignRequest, DocumentLabelDto, DocumentTemplateType, EnergyCommunityDocumentLabelDto, ResolvedBrandTemplateDto} from "@/models/dtos/documentLabelDto";
import type {MailPreviewDto, MailPreviewScope, MailPreviewScopeKind, SendTestMailRequestDto} from "@/models/dtos/mailPreviewDto";
import type {InvoicePreviewDto} from "@/models/dtos/invoicePreviewDto";
import type {DeactivateAccountResultDto, ReactivateAccountResultDto} from "@/models/dtos/accountLifecycleDto";
import type {BrandAccountPageDto, BrandAdminDto, BrandCommunityPageDto, BrandSaveResultDto, BrandSetupCheckDto, BrandSetupCheckRequestDto, BrandThemeSuggestionDto, CreateBrandRequestDto, DocumentLabelOptionDto, UpdateBrandRequestDto} from "@/models/dtos/brandAdminDto";
import buildQuery from "odata-query";


// Creating an instance for axios to be used by the token interceptor service
const authInstance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    headers: {
        "Content-Type": "application/json",
    },
});

const publicInstance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    headers: {
        "Content-Type": "application/json",
    },
});

async function initialize(){
    setupTokenInterceptors(authInstance);
}

async function publicPost<Type>(route: string, body?: any) {
    //console.log(`posting ${route}...`)
    const response = await publicInstance.post<Type>(route, body); // Replace 'your-endpoint' with the actual endpoint
    return response.data; // Return the data received from the API
}

async function get<Type>(route: string, config?: any) {
    console.log(`fetching ${route}...`);
    const response = await authInstance.get<Type>(route, config);

    return response.data; // Return the data received from the API
}

async function post<Type>(route: string, body?: any) {
    //console.log(`posting ${route}...`)
    const response = await authInstance.post<Type>(route, body); // Replace 'your-endpoint' with the actual endpoint
    return response.data; // Return the data received from the API
}

async function patch<Type>(route: string, body?: any) {
    //console.log(`patching ${route}...`)
    const response = await authInstance.patch<Type>(route, body);
    return response.data;
}

async function put<Type>(route: string, body?: any) {
    //console.log(`putting ${route}...`)
    const response = await authInstance.put<Type>(route, body);
    return response.data;
}

async function del<Type>(route: string) {
    //console.log(`deleting ${route}...`)
    const response = await authInstance.delete<Type>(route);
    return response.data; // Return the data received from the API
}

async function odataDelete<Type>(route: string) {
    console.log(`deleting ${route}...`);
    const response = await authInstance.delete<Type>(route);
    return response.data; // Return the data received from the API
}

async function odataPost<Type>(route: string, body?: any) {
    //console.log(`posting ${route}...`)
    const response = await authInstance.post<Type>(route, body); // Replace 'your-endpoint' with the actual endpoint
    return response.data; // Return the data received from the API
}

async function odataPatch<Type>(route: string, body?: any) {
    //console.log(`patching ${route}...`)
    const response = await authInstance.patch<Type>(route, body); // Replace 'your-endpoint' with the actual endpoint
    return response.data; // Return the data received from the API
}

async function odataGet<Type>(route: string, config?: any) {
    console.log(`fetching ${route}...`);
    const response = await authInstance.get<Type>(route, config);

    // Special OData handling for collections
    if (response.data && response.data && Array.isArray(response.data)) {
        return response.data;
    }

    // Handle OData single entity response
    if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        //console.log('Returning object data from response');
        return response.data;
    }


    throw new Error('Unexpected OData response format');
}

async function dashboardData() {
    return get<DashboardStats>('/v1/Sonnnig/dashboardStats');
}

async function communityDashboardData() {
    return get<CommunityDashboardStatsDto>('/v1/Sonnnig/communityDashboardStats');
}

async function identity() {
    return get<IdentityDto>('/v1/Sonnnig/identity'); // Replace 'your-endpoint' with the actual endpoint
}

async function onboarding(data: OnboardingDataDto) {
    return await odataPost('/v1/Sonnnig/onboarding', data); // Replace 'your-endpoint' with the actual endpoint
}

async function publicOnboarding(data: PublicOnboardingRequestDto) {
    return await publicPost('/v1/Sonnnig/publicOnboarding', data);
}

async function getPublicOnboardingConfig(slug: string) {
    const response = await publicInstance.get<OnboardingConfigDto>(`/v1/Sonnnig/publicOnboarding/active?communitySlug=${slug}`);
    return response.data;
}

async function confirmSubmission(confirmationId: string) {
    const response = await publicInstance.post<SubmissionConfirmationResponse>(`/v1/Sonnnig/publicOnboarding/confirm/${confirmationId}`);
    return response.data;
}

async function submitBankData(confirmationId: string, bankData: BankDataDto) {
    const response = await publicInstance.post<BankDataSubmissionResponse>(`/v1/Sonnnig/publicOnboarding/submit-bank-data/${confirmationId}`, bankData);
    return response.data;
}

async function resetUser() {
    return await post('/v1/Test/resetUser'); // Replace 'your-endpoint' with the actual endpoint
}

async function tryGetEnergyCommunity(communityId: string){
    try {
        return await get<EnergyCommunityDto>(`odata/EnergyCommunity/${communityId}`);
    }
    catch{
        return null;
    }
}

async function getEnergyCommunities(options?: any) {
    const query = options ? buildQuery(options) : '';
    return get<EnergyCommunityDto[]>(`odata/EnergyCommunity${query}`);
}

async function getEnergyCommunityWithMeteringPoints(communityId: string) : Promise<EnergyCommunityDto> {
    const query = buildQuery({
        expand: {
            MeteringPointMemberships: {
                expand: {
                    MeteringPoint: {
                        select: ['Id', 'MeteringPointNumber', 'Direction', 'ProductionType', 'ProductionPower', 'YearlyEnergyFlow', 'PremiseId', 'Premise'],
                        expand: {
                            Premise: {
                                select: ['Id', 'Street', 'City', 'State', 'Zip', 'Country', 'HouseNumber', 'StairNumber', 'DoorNumber', 'AddressAddition', 'FloorNumber'],
                                expand: {
                                    User: {
                                        select: ['Id', 'FirstName', 'LastName', 'CompanyName']
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    });

    return get(`odata/EnergyCommunity/${communityId}${query}`);
}

async function sendEnergyCommunityApplication(communityId: string){
    return await odataPost(`odata/energy-community-application`, {energyCommunityId: communityId});
}

async function sendHabit(habitType: string){
    return await post(`odata/Habit`, {type: habitType});
}

async function getHabits(clause?: string){
    if(clause)
        return await get(`odata/Habit?${clause}`);

    return await get(`odata/Habit`);
}

async function getMeteringPoints(clause: string){
    return await get<MeteringPointDto[]>(`odata/metering-points`);
}

async function getMeteringPointNumbers() {
    const query = buildQuery({
        select: ['Id', 'MeteringPointNumber'],
        expand: {
            Premise: {
                expand: {
                    User: {
                        select: ['FirstName', 'LastName']
                    }
                }
            }
        }
    });
    return await get<{id: string, meteringPointNumber: string, premise?: {user?: {firstName: string, lastName: string}}}[]>(`odata/MeteringPoint${query}`);
}

async function getMeteringPointMemberships(options?: any) {
    const query = options ? buildQuery(options) : '';
    return get(`odata/metering-point-memberships${query}`);
}


async function energyTimeSeries(meterId: string, from?: Date, to?: Date){
    return await get<EnergyUsageDto>("/v1/Sonnnig/energyUsage", {params: {
            meterId,
            from,
            to
        }});
}

async function energyDataAvailability(energyCommunityId: string, from?: Date, to?: Date, page: number = 1, pageSize: number = 20, search?: string){
    return await get<EnergyDataAvailabilityDto>("/v1/EnergyData/energy-data-availability", {params: {
            energyCommunityId,
            from,
            to,
            page,
            pageSize,
            search: search || undefined
        }});
}

async function energyCommunityDataSummary(energyCommunityId: string, from?: Date, to?: Date){
    return await get<EnergyCommunityDataSummaryDto>("/v1/EnergyData/energy-community-data-summary", {params: {
            energyCommunityId,
            from,
            to
        }});
}

async function energyDataRequestOverview(query: EnergyDataRequestOverviewQuery){
    return await get<EnergyDataRequestOverviewDto>("/v1/EnergyData/energy-data-request-overview", {params: {
            energyCommunityId: query.energyCommunityId,
            from: query.from,
            to: query.to,
            meteringPoint: query.meteringPoint || undefined,
            dataFrom: query.dataFrom || undefined,
            dataTo: query.dataTo || undefined
        }});
}

async function canForecast(energyCommunityId: string, forecastStart?: Date){
    return await get<EnergyDataAvailabilityDto>("/v1/EnergyData/can-forecast", {params: {
            energyCommunityId,
            forecastStart
        }});
}

async function canNotify(energyCommunityId: string, start: string, end: string){
    return await get<NotificationPossibleDto>("/v1/EnergyData/can-notify", {params: {
            energyCommunityId,
            start,
            end
        }});
}

// System event (audit trail) functions
async function getSystemEvents(query: SystemEventQueryDto): Promise<SystemEventPageDto> {
    const filters: unknown[] = [];

    // An `or` of `eq` comparisons instead of `in`: the OData endpoint exposes `type` as an enum and
    // handles the single-value comparison reliably.
    if (query.types.length > 0) {
        filters.push({or: query.types.map(type => ({type: {eq: type}}))});
    }
    if (query.meterId) {
        filters.push({meterId: {contains: query.meterId}});
    }
    if (query.marketPartnerId) {
        filters.push({marketPartnerId: {contains: query.marketPartnerId}});
    }
    if (query.metaDataId) {
        filters.push({metaDataId: {contains: query.metaDataId}});
    }

    const odataQuery = buildQuery({
        count: true,
        skip: query.skip,
        top: query.top,
        orderBy: [`${query.sortField} ${query.sortDescending ? 'desc' : 'asc'}`],
        filter: filters.length > 0 ? {and: filters} : undefined
    });

    const response: any = await odataGet(`odata/SystemEvent${odataQuery}`);

    return {
        items: (response?.value ?? []) as SystemEventDto[],
        totalCount: response?.['@odata.count'] ?? 0
    };
}

/** Distinct event types that actually occur in the database, alphabetically sorted. */
async function getSystemEventTypes(): Promise<string[]> {
    const response: any = await odataGet(`odata/SystemEvent?$apply=groupby((type))`);
    const entries: { type?: string }[] = response?.value ?? response ?? [];

    return entries
        .map(entry => entry.type)
        .filter((type): type is string => !!type)
        .sort((a, b) => a.localeCompare(b));
}

// EdaProcessViews Conversation and Message functions
async function getEdaConversations(options?: any) : Promise<EdaConversationDto[]> {
    // Create default options if none provided
    const defaultOptions = {
        expand: { messages: {} }
    };
    
    // Use the provided options or default ones
    const finalOptions = options || defaultOptions;
    
    // Build the query using odata-querys
    const query = buildQuery(finalOptions);
    
    const response: any = await odataGet(`odata/EdaConversation${query}`);
    return response.value || response;
}

async function getEdaConversation(id: string) {
    // Build the query using odata-query
    const query = buildQuery({
        expand: { messages: {} }
    });
    
    return get<EdaConversationDto>(`odata/EdaConversation/${id}${query}`);
}

function edaMessageSelectString() {
    return `id,messageVersion,backendReceiverId,schemaSet,externalMessageId,pontonMessageId,filename,contentType,messageType,backendSenderId,processedAt,errorMessage,externalConversationId,edaConversationId,marketPartnerId,createdOn,updatedOn`;
}

async function closeEdaConversation(id: string) {
    return odataPatch(`odata/EdaConversation/${id}`, {isClosed: true});
}

async function getEdaMessages() {
    return get<EdaMessageDto[]>('odata/EdaMessage');
}

async function getEdaMessagesByConversation(conversationId: string) {
    const query = buildQuery({
        filter: { conversationId: { eq: { type: 'guid', value: conversationId } } }
    });
    
    return get<EdaMessageDto[]>(`odata/EdaMessage${query}`);
}

// Send an EdaProcessViews message
async function sendEdaMessage(messageId: string) {
    console.log(`Sending EDA message:`, messageId);
    const response = await authInstance.post(`/v1/Ponton/send-message/${messageId}`);
    return response.data;
}

// Send all pending messages in a conversation
async function sendPendingEdaMessages(conversationId: string) {
    console.log(`Sending all pending messages in conversation:`, conversationId);
    const response = await authInstance.post(`/v1/Ponton/send-conversation-messages/${conversationId}`);
    return response.data;
}

// Reprocess selected EdaProcessViews conversations
async function reprocessConversations(conversationIds: string[], onlyUnprocessed: boolean = true) {
    console.log(`Reprocessing conversations:`, conversationIds, `onlyUnprocessed: ${onlyUnprocessed}`);
    const requestBody = {
        conversationIds: conversationIds,
        onlyUnprocessed: onlyUnprocessed
    };
    const response = await authInstance.post('/v1/Ponton/reprocess-all', requestBody);
    return response.data;
}

// Reprocess specific messages
async function reprocessMessages(messageIds: string[]) {
    console.log(`Reprocessing messages:`, messageIds);
    const response = await authInstance.post('/v1/Ponton/reprocess-messages', messageIds);
    return response.data;
}

// Test controller endpoints for PODList, Community Enrollment, and Energy Data Request
async function requestPodList(request: {
    partnerId: string;
    remotePartnerId?: string | null;
    from?: string;
    to?: string;
}): Promise<PontonProcessResponseDto> {
    console.log(`Requesting POD list sync:`, request);
    const response = await authInstance.post('/v1/Ponton/podlist', request);
    return response.data;
}

async function requestCommunityEnrollment(params: {
    energyCommunityId: string;
    meteringPointId: string;
    participationFactor: number;
    direction: string;
    from?: string;
    consentId?: string;
    offlineRequest: boolean;
    dryRun: boolean;
}): Promise<PontonProcessResponseDto> {
    console.log(`Requesting community enrollment with params:`, params);
    const response = await authInstance.post('/v1/Ponton/community-enrollment', params);
    return response.data;
}

async function requestEnergyData(params: {
    energyCommunityId: string;
    from?: string;
    to?: string;
    loadProfile?: string | null;
    meterCode: string;
    remotePartnerId?: string;
    dryRun: boolean;
}): Promise<PontonProcessResponseDto> {
    console.log(`Requesting energy data with params:`, params);
    const response = await authInstance.post('/v1/Ponton/energy-data', params);
    return response.data;
}

// Action to add a premise to a metering point
async function addMeteringPointPremise(meteringPointId: string, premiseData: any) {
    console.log(`Adding premise to metering point:`, premiseData);
    const response = await authInstance.post(`odata/MeteringPoint/${meteringPointId}/Premise`, premiseData);
    return response.data;
}

// Action to update an existing premise
async function updatePremise(premiseId: string, premiseData: any) {
    console.log(`Updating premise:`, premiseData);
    const response = await authInstance.patch(`odata/Premise/${premiseId}`, premiseData);
    return response.data;
}

// Action to add a user to a premise
async function addPremiseUser(premiseId: string, userData: any) {
    console.log(`Adding user to premise:`, userData);
    const response = await authInstance.post(`odata/Premise/${premiseId}/User`, userData);
    return response.data;
}

// Action to update an existing user
async function updateUser(userId: string, userData: any) {
    console.log(`Updating user:`, userData);
    const response = await authInstance.patch(`odata/SonnnigUser/${userId}`, userData);
    return response.data;
}

// Get all premises with users
async function getAllPremises(includeUsers = true) {
    const query = buildQuery({
        expand: includeUsers ? { User: {} } : undefined,
        select: ['Id', 'Street', 'HouseNumber', 'City', 'Zip', 'User']
    });
    return get(`odata/Premise${query}`);
}

// Get community users (navigating through metering points and premises)
async function getCommunityUsers(communityId: string) {
    const query = buildQuery({
        expand: {
            MeteringPointMemberships: {
                expand: {
                    MeteringPoint: {
                        expand: {
                            Premise: {
                                expand: {
                                    User: {
                                        select: ['Id', 'FirstName', 'LastName', 'Email', 'PhoneNumber']
                                    }
                                },
                                select: ['Id', 'Street', 'City', 'Zip', 'HouseNumber', 'User']
                            }
                        },
                        select: ['Id', 'MeteringPointNumber', 'Direction', 'Premise']
                    }
                }
            }
        }
    });
    
    return get(`odata/EnergyCommunity/${communityId}${query}`);
}

// Get user details with premises and metering points
async function getUserDetails(userId: string) {
    const query = buildQuery({
        expand: {
            Premises: {
                expand: {
                    MeteringPoints: {}
                }
            }
        }
    });
    
    return get(`odata/SonnnigUser/${userId}${query}`);
}

// Create a new premise
async function createPremise(premiseData: any) {
    console.log(`Creating new premise:`, premiseData);
    const response = await authInstance.post(`odata/Premise`, premiseData);
    return response.data;
}

// Move metering point to a different premise
async function moveMeteringPointToPremise(meteringPointId: string, premiseId: string) {
    console.log(`Moving metering point ${meteringPointId} to premise ${premiseId}`);
    // This is a PATCH operation to update the premise reference
    const response = await authInstance.patch(`odata/MeteringPoint/${meteringPointId}`, {
        premiseId: premiseId
    });
    return response.data;
}

// Create a new metering point
async function createMeteringPoint(meteringPointData: any) {
    console.log(`Creating new metering point:`, meteringPointData);
    const response = await authInstance.post(`odata/MeteringPoint`, meteringPointData);
    return response.data;
}

// Update an existing metering point
async function updateMeteringPoint(meteringPointId: string, meteringPointData: any) {
    console.log(`Updating metering point:`, meteringPointData);
    const response = await authInstance.patch(`odata/MeteringPoint/${meteringPointId}`, meteringPointData);
    return response.data;
}

// Delete a metering point
async function deleteMeteringPoint(meteringPointId: string) {
    console.log(`Deleting metering point: ${meteringPointId}`);
    const response = await authInstance.delete(`odata/MeteringPoint/${meteringPointId}`);
    return response.data;
}

// Delete a premise
async function deletePremise(premiseId: string) {
    console.log(`Deleting premise: ${premiseId}`);
    const response = await authInstance.delete(`odata/Premise/${premiseId}`);
    return response.data;
}

// Export CSV for energy community
async function exportCsv(params: { slug: string; from: string; to: string }) {
    console.log(`Exporting CSV for community:`, params);
    const response = await authInstance.get('/v1/EnergyData/export-csv', {
        params,
        responseType: 'blob' // Important to handle binary response
    });
    return response;
}

async function exportTimeSeriesCsv(params: { slug: string; from: string; to: string }) {
    console.log(`Exporting Timeseries for community:`, params);
    const response = await authInstance.get('/v1/EnergyData/export-timeseries', {
        params,
        responseType: 'blob' // Important to handle binary response
    });
    return response;
}

async function logAnalyticsEvent(event: AnalyticsEventDto) {
    try {
        await publicPost('/v1/Sonnnig/logAnalyticsEvent', event);
    } catch (error) {
        // Silent fail for analytics - don't interrupt user flow
        console.warn('Failed to log analytics event:', error);
    }
}

async function processOnboardingSubmissions(request: ProcessOnboardingRequestDto) {
    console.log(`Processing onboarding submissions:`, request.submissionIds);
    return post<ProcessOnboardingResponseDto>('/v1/Sonnnig/publicOnboarding/process', request);
}

async function requestCommunityWithdraw(request: CommunityWithdrawalRequestDto): Promise<PontonProcessResponseDto> {
    console.log('Requesting community withdrawal:', request);
    const response = await authInstance.post('/v1/Ponton/community-withdraw', request);
    return response.data;
}

async function changeParticipationFactor(request: ParticipationFactorChangeRequestDto): Promise<PontonProcessResponseDto> {
    console.log('Changing participation factor:', request);
    const response = await authInstance.post('/v1/Ponton/change-participation-factor', request);
    return response.data;
}

// Request missing energy data for entire community
async function requestMissingEnergyData(request: RequestMissingDataDto) {
    console.log('Requesting missing energy data for community:', request);
    const response = await authInstance.post('/v1/EnergyData/request-missing-data', request);
    return response;
}

// Get user recommendation
async function getUserRecommendation(communityId: string, userId: string, date: string, resultType: number | null) {
    console.log(`Getting recommendation for user ${userId}, date: ${date}, type: ${resultType}`);
    const params = new URLSearchParams({
        communityId,
        userId,
        date
    });
    if (resultType !== null) {
        params.append('resultType', resultType.toString());
    }
    return get<RecommendationResponseDto>(`/v1/DigitalTwin/get-recommendation?${params.toString()}`);
}

// Get community recommendation
async function getCommunityRecommendation(communityId: string, date: string) {
    const params = new URLSearchParams({ communityId, date });
    return get<CommunityRecommendationResponseDto>(`/v1/DigitalTwin/get-community-recommendation?${params.toString()}`);
}

// Send recommendation to a single user
async function sendRecommendation(
    communityId: string,
    userId: string,
    date: string,
    resultType: number | null,
    allowedLowestCategory: number
) {
    console.log(`Sending recommendation to user ${userId} in community ${communityId}`);
    return post(`/v1/DigitalTwin/send-recommendation`, {
        communityId,
        userId,
        date,
        resultType,
        allowedLowestCategory
    });
}

// Send recommendations to all users in a community: creates or reuses the current cycle's
// Pending run and runs the notifyability check. Throws when a Sent/Skipped run already exists
// for the cycle — delete it first (deleteWeeklyRecommendationRun) to send again.
async function sendRecommendationCommunity(
    communityId: string,
    date: string,
    resultType: number | null,
    allowedLowestCategory?: number
) {
    console.log(`Sending recommendations to community ${communityId}`);
    return post<WeeklyRecommendationRunDto>(
        `/v1/DigitalTwin/energy-communities/${communityId}/weekly-recommendation-runs`,
        { date, resultType, allowedLowestCategory }
    );
}

// Weekly recommendation runs across communities, newest cycle first (then by community name).
// communityId/state narrow the result; both are optional so the App's cross-community list can
// show everything at once.
async function getWeeklyRecommendationRuns(filter?: {
    communityId?: string | null;
    state?: WeeklyRecommendationRunState | null;
    take?: number;
}) {
    const params = new URLSearchParams();
    if (filter?.communityId) params.append('communityId', filter.communityId);
    if (filter?.state) params.append('state', filter.state);
    if (filter?.take) params.append('take', filter.take.toString());

    const query = params.toString();
    return get<WeeklyRecommendationRunDto[]>(
        `/v1/DigitalTwin/weekly-recommendation-runs${query ? `?${query}` : ''}`
    );
}

// Force-send a Pending run without the notifyability check (admin override).
async function forceSendWeeklyRecommendationRun(
    communityId: string,
    runId: string,
    resultType: number | null,
    allowedLowestCategory?: number
) {
    return post<WeeklyRecommendationRunDto>(
        `/v1/DigitalTwin/energy-communities/${communityId}/weekly-recommendation-runs/${runId}/force-send`,
        { resultType, allowedLowestCategory }
    );
}

// Delete a run regardless of its state — the way to allow a second send for an already Sent
// cycle, or to stop retries of a Pending one.
async function deleteWeeklyRecommendationRun(communityId: string, runId: string) {
    return del(`/v1/DigitalTwin/energy-communities/${communityId}/weekly-recommendation-runs/${runId}`);
}

// Pure admin override: sets a run's State directly — no send, no notifyability check. Setting
// Sent does not trigger any dispatch; setting Pending on an older cycle gets flipped back to
// Skipped by the recurring job's stale-run cleanup once a newer cycle exists.
async function setWeeklyRecommendationRunState(
    communityId: string,
    runId: string,
    state: WeeklyRecommendationRunState
) {
    return patch<WeeklyRecommendationRunDto>(
        `/v1/DigitalTwin/energy-communities/${communityId}/weekly-recommendation-runs/${runId}`,
        { state }
    );
}

// Check billing possibility for a community and time range
async function checkBillability(request: BillabilityRequestDto) {
    console.log('Checking billability for community:', request);
    return post<BillabilityDto>('/v1/Sonnnig/billing/possible', request);
}

// Energiegraph (Zeitreihe + KPIs) einer Gemeinschaft oder ausgewählter Zählpunkte.
// Liefert je Bucket zusätzlich Erwartungswert, Allokationskonsistenz und Plausibilität.
async function getEnergyGraph(
    energyCommunityId: string,
    request: Omit<EnergyGraphRequestDto, 'energyCommunityId'>
): Promise<EnergyGraphResponseDto> {
    return post<EnergyGraphResponseDto>(`/v1/eeg/${energyCommunityId}/energy-graph`, {
        ...request,
        energyCommunityId
    });
}

// Get energy timeseries for a specific metering point
async function getEnergyTimeSeries(
    energyCommunity: string,
    meterId: string,
    from: Date,
    to: Date,
    granularity: TimeseriesGranularity = 1 // DayView as default
): Promise<EnergyTimeSeriesDto> {
    console.log(`Getting energy timeseries for metering point ${meterId}`);
    return get<EnergyTimeSeriesDto>('/v1/EnergyData/energy-timeseries', {
        params: {
            energyCommunity,
            meterId,
            from: from.toISOString(),
            to: to.toISOString(),
            granularity
        }
    });
}

// Financial breakdown + billing sanity checks of a run (super admin / developer only).
async function getInvoiceRunBreakdown(invoiceRunId: string) {
    return get<InvoiceRunBreakdownDto>(`/v1/invoice-runs/${invoiceRunId}/breakdown`);
}

// Trigger billing run
async function triggerBilling(request: TriggerBillingDto) {
    console.log('Triggering billing run for community:', request);
    return post(`/v1/Sonnnig/billing/trigger`, request);
}

async function queueInvoiceDocuments(invoiceRunId: string, request: QueueInvoiceDocumentsRequestDto) {
    return post(`/v1/Sonnnig/billing/${invoiceRunId}/queue-documents`, request);
}

// AdminDashboard-only invoice run management (super admin / developer token).
// Lists every run for the community (not just scheduled) with full granular detail.
async function getAdminInvoiceRuns(energyCommunityId: string): Promise<AdminInvoiceRunDto[]> {
    const response = await get<AdminInvoiceRunListResponseDto>('/v1/invoice-runs/admin', {
        params: { energyCommunityId }
    });
    return response.data ?? [];
}

// Re-drive the run's state machine (precondition recheck / advance).
async function recheckInvoiceRun(invoiceRunId: string) {
    return post(`/v1/invoice-runs/${invoiceRunId}/recheck`);
}

// Force a run stuck on failed billability into billing, bypassing the eligibility gate.
async function forceAdvanceInvoiceRun(invoiceRunId: string) {
    return post(`/v1/invoice-runs/${invoiceRunId}/force-advance`);
}

// Re-bill the run's period as a separate Recalculation run (energy only, stops at Calculated).
async function recalculateInvoiceRun(invoiceRunId: string) {
    return post(`/v1/invoice-runs/${invoiceRunId}/recalculate`);
}

// Energy-component diff of a Recalculation run against the run that billed the same period.
async function getInvoiceRunComparison(invoiceRunId: string) {
    return get<InvoiceRunComparisonDto>(`/v1/invoice-runs/${invoiceRunId}/comparison`);
}

// Release a Ready run: hands it to the batch send, which mails every invoice of the run.
async function confirmInvoiceRun(invoiceRunId: string) {
    return post(`/v1/invoice-runs/${invoiceRunId}/confirm`);
}

// Hard-delete a run and its invoices.
async function deleteInvoiceRun(invoiceRunId: string) {
    return del(`/v1/invoice-runs/${invoiceRunId}`);
}

// Toggle whether the run is listed to dashboard users (origin Scheduled vs Manual).
async function setInvoiceRunVisibility(invoiceRunId: string, visible: boolean) {
    return patch(`/v1/invoice-runs/${invoiceRunId}/visibility`, { visible });
}

// Unified, paged overview of every invoice run across all communities (Admin Billing view).
async function getAdminInvoiceRunOverview(params: {
    page?: number;
    pageSize?: number;
    energyCommunityId?: string | null;
    status?: string | null;
    onlyBlocked?: boolean;
    search?: string | null;
}): Promise<PagedResult<AdminInvoiceRunOverviewDto>> {
    const query: Record<string, any> = {
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 50,
        onlyBlocked: params.onlyBlocked ?? false,
    };
    if (params.energyCommunityId) query.energyCommunityId = params.energyCommunityId;
    if (params.status) query.status = params.status;
    if (params.search) query.search = params.search;
    return get<PagedResult<AdminInvoiceRunOverviewDto>>('/v1/invoice-runs/admin/overview', { params: query });
}

// The persisted master-data to-dos of a single run (which user / metering point is missing what).
async function getInvoiceRunTodos(invoiceRunId: string): Promise<InvoiceRunTodoDto[]> {
    const response = await get<InvoiceRunTodoListResponseDto>(`/v1/invoice-runs/${invoiceRunId}/todos`);
    return response.data ?? [];
}

// AdminDashboard-only tariff management (super admin / developer token).
// Lists both tariff types of a community with resolved scope names, lock info and coverage warnings.
async function getCommunityTariffs(energyCommunityId: string): Promise<CommunityTariffsResponseDto> {
    return get<CommunityTariffsResponseDto>('/v1/tariffs', { params: { energyCommunityId } });
}

async function createFixedTariff(request: CreateFixedTariffDto): Promise<FixedTariffDto> {
    return post<FixedTariffDto>('/v1/tariffs/fixed', request);
}

async function updateFixedTariff(tariffId: string, request: UpdateFixedTariffDto): Promise<FixedTariffDto> {
    return put<FixedTariffDto>(`/v1/tariffs/fixed/${tariffId}`, request);
}

// The only mutation allowed on a tariff that has already been invoiced (null = reopen).
async function setFixedTariffEndDate(tariffId: string, endDate: string | null): Promise<FixedTariffDto> {
    return patch<FixedTariffDto>(`/v1/tariffs/fixed/${tariffId}/end-date`, { endDate });
}

async function deleteFixedTariff(tariffId: string) {
    return del(`/v1/tariffs/fixed/${tariffId}`);
}

async function createFlatRateTariff(request: CreateFlatRateTariffDto): Promise<FlatRateTariffDto> {
    return post<FlatRateTariffDto>('/v1/tariffs/flat-rate', request);
}

async function updateFlatRateTariff(tariffId: string, request: UpdateFlatRateTariffDto): Promise<FlatRateTariffDto> {
    return put<FlatRateTariffDto>(`/v1/tariffs/flat-rate/${tariffId}`, request);
}

async function setFlatRateTariffEndDate(tariffId: string, endDate: string | null): Promise<FlatRateTariffDto> {
    return patch<FlatRateTariffDto>(`/v1/tariffs/flat-rate/${tariffId}/end-date`, { endDate });
}

async function deleteFlatRateTariff(tariffId: string) {
    return del(`/v1/tariffs/flat-rate/${tariffId}`);
}

// ---- Onboarding form configuration (/v1/eeg/{eegId}/onboarding-form) ----

// Returns every item, including the scheduled and the expired ones — the configurator has to show
// and be able to revive those.
async function getOnboardingForm(energyCommunityId: string): Promise<OnboardingFormItemDto[]> {
    return get<OnboardingFormItemDto[]>(`/v1/eeg/${energyCommunityId}/onboarding-form`);
}

// Whole-list replace: the position in the list becomes the item's sort order, and items left out
// are deleted (which the backend refuses for items applicants have already answered).
async function saveOnboardingForm(
    energyCommunityId: string,
    items: OnboardingFormItemWriteDto[]
): Promise<OnboardingFormItemDto[]> {
    return put<OnboardingFormItemDto[]>(`/v1/eeg/${energyCommunityId}/onboarding-form`, items);
}

// User transactions (Vorauszahlungen / credits) — developer-only tooling
async function getUserTransactions(userId?: string): Promise<UserTransactionDto[]> {
    const params = userId ? { userId } : undefined;
    return get<UserTransactionDto[]>('/v1/user-transactions', { params });
}

async function createUserTransaction(request: CreateUserTransactionDto): Promise<UserTransactionDto> {
    return post<UserTransactionDto>('/v1/user-transactions', request);
}

async function updateUserTransaction(transactionId: string, request: UpdateUserTransactionDto): Promise<UserTransactionDto> {
    return put<UserTransactionDto>(`/v1/user-transactions/${transactionId}`, request);
}

async function deleteUserTransaction(transactionId: string) {
    return del(`/v1/user-transactions/${transactionId}`);
}

// Migrate membership data to another user
async function migrateMembershipToUser(request: { membershipId: string; userId: string }) {
    console.log('Migrating membership to user:', request);
    return post('/v1/Migration/migrate-membership-to-user', request);
}

// The brands an admin can pick when inviting accounts or creating users
async function getWhitelabels() {
    return get<BrandOptionDto[]>('/v1/Permission/whitelabels');
}

// Brand administration (super admin): master data of the whitelabels
async function getBrands() {
    return get<BrandAdminDto[]>('/v1/admin/brands');
}

async function getBrand(brandId: string) {
    return get<BrandAdminDto>(`/v1/admin/brands/${brandId}`);
}

// The energy communities assigned to a brand, one page at a time, optionally narrowed by name or slug
async function getBrandCommunities(brandId: string, skip: number, top: number, search?: string): Promise<BrandCommunityPageDto> {
    const term = search?.trim().toLowerCase();
    const brandFilter = {whitelabelId: {eq: {type: 'guid', value: brandId}}};
    const odataQuery = buildQuery({
        count: true,
        skip,
        top,
        orderBy: ['name asc'],
        select: ['Id', 'Name', 'Slug', 'CreatedOn'],
        filter: term
            ? {and: [brandFilter, {or: [{'tolower(name)': {contains: term}}, {'tolower(slug)': {contains: term}}]}]}
            : brandFilter
    });

    const response: any = await odataGet(`odata/EnergyCommunity${odataQuery}`);

    return {
        items: response?.value ?? [],
        totalCount: response?.['@odata.count'] ?? 0
    };
}

// The accounts of a brand with the users behind them, one page at a time, optionally narrowed by user name, email or name.
// Deactivated accounts are left out unless asked for.
async function getBrandAccounts(brandId: string, skip: number, top: number, search?: string,
                                includeDeactivated = false): Promise<BrandAccountPageDto> {
    const term = search?.trim().toLowerCase();
    const brandFilter = includeDeactivated
        ? {whitelabelId: {eq: {type: 'guid', value: brandId}}}
        : {and: [{whitelabelId: {eq: {type: 'guid', value: brandId}}}, {deactivatedOn: {eq: null}}]};
    const odataQuery = buildQuery({
        count: true,
        skip,
        top,
        orderBy: ['userName asc'],
        select: ['Id', 'UserName', 'Email', 'Name', 'SubjectId', 'DeactivatedOn', 'Users'],
        expand: {Users: {select: ['Id', 'Email']}},
        filter: term
            ? {and: [brandFilter, {or: [
                {'tolower(userName)': {contains: term}},
                {'tolower(email)': {contains: term}},
                {'tolower(name)': {contains: term}}
            ]}]}
            : brandFilter
    });

    const response: any = await odataGet(`odata/AccountOData${odataQuery}`);

    return {
        items: response?.value ?? [],
        totalCount: response?.['@odata.count'] ?? 0
    };
}

// The theme presets no brand uses yet, with the Keycloak coordinates the conventions suggest
async function getBrandThemes() {
    return get<BrandThemeSuggestionDto[]>('/v1/admin/brands/themes');
}

// The Keycloak coordinates the conventions suggest for any brand key, with or without a preset
async function getBrandSuggestion(key: string) {
    return get<BrandThemeSuggestionDto>(`/v1/admin/brands/suggestion?key=${encodeURIComponent(key)}`);
}

async function getBrandDocumentLabels() {
    return get<DocumentLabelOptionDto[]>('/v1/admin/brands/document-labels');
}

async function createBrand(request: CreateBrandRequestDto) {
    return post<BrandSaveResultDto>('/v1/admin/brands', request);
}

async function updateBrand(brandId: string, request: UpdateBrandRequestDto) {
    return patch<BrandSaveResultDto>(`/v1/admin/brands/${brandId}`, request);
}

// Only for a brand without accounts and energy communities; its Keycloak realm stays
async function deleteBrand(brandId: string) {
    return del<void>(`/v1/admin/brands/${brandId}`);
}

// Non-blocking check of issuer, realm, client and redirect URIs for the given coordinates
async function checkBrandSetup(request: BrandSetupCheckRequestDto) {
    return post<BrandSetupCheckDto>('/v1/admin/brands/setup-check', request);
}

// Creates a brand together with a new document design and its template files, all or nothing
async function createBrandWithDesign(request: CreateBrandWithDesignRequest) {
    const formData = new FormData();
    formData.append('Key', request.key);
    formData.append('Name', request.name);
    formData.append('SupportEmail', request.supportEmail);
    if (request.logoUrl) formData.append('LogoUrl', request.logoUrl);
    if (request.privacyPolicyUrl) formData.append('PrivacyPolicyUrl', request.privacyPolicyUrl);
    formData.append('FrontendBaseUrl', request.frontendBaseUrl);
    formData.append('Realm', request.realm);
    formData.append('IssuerUrl', request.issuerUrl);
    formData.append('ClientId', request.clientId);
    formData.append('DocumentLabelName', request.documentLabelName);
    if (request.invoiceFile) formData.append('InvoiceFile', request.invoiceFile);
    if (request.invoiceMailFile) formData.append('InvoiceMailFile', request.invoiceMailFile);
    if (request.adminNotificationFile) formData.append('AdminNotificationFile', request.adminNotificationFile);

    const response = await authInstance.post<BrandSaveResultDto>('/v1/admin/brands/with-design', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
}

// The communities of a brand that use a document design of their own
async function getBrandCommunityDesigns(brandId: string) {
    return get<BrandCommunityDesignDto[]>(`/v1/admin/brands/${brandId}/community-designs`);
}

// Document designs (super admin): the named groups of templates brands and communities point at
async function getDocumentLabels() {
    return get<DocumentLabelDto[]>('/v1/admin/document-labels');
}

async function getDocumentLabel(labelId: string) {
    return get<DocumentLabelDto>(`/v1/admin/document-labels/${labelId}`);
}

async function createDocumentLabel(name: string) {
    return post<DocumentLabelDto>('/v1/admin/document-labels', { name });
}

async function renameDocumentLabel(labelId: string, name: string) {
    return patch<DocumentLabelDto>(`/v1/admin/document-labels/${labelId}`, { name });
}

async function deleteDocumentLabel(labelId: string) {
    return del(`/v1/admin/document-labels/${labelId}`);
}

// Checks a template file without saving it; rejects with the reason when the file is unusable
async function validateBrandTemplate(type: DocumentTemplateType, file: File) {
    const formData = new FormData();
    formData.append('Type', type);
    formData.append('File', file);

    await authInstance.post('/v1/admin/document-labels/validate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
}

async function uploadBrandTemplate(labelId: string, type: DocumentTemplateType, file: File) {
    const formData = new FormData();
    formData.append('File', file);

    const response = await authInstance.put<BrandTemplateDto>(
        `/v1/admin/document-labels/${labelId}/templates/${type}`, formData,
        { headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data;
}

async function removeBrandTemplate(labelId: string, type: DocumentTemplateType) {
    return del(`/v1/admin/document-labels/${labelId}/templates/${type}`);
}

// Sets (or, with null, clears) the document design of one community
async function setEnergyCommunityDocumentLabel(energyCommunityId: string, documentLabelId: string | null) {
    return put<EnergyCommunityDocumentLabelDto>(
        `/v1/admin/energy-communities/${energyCommunityId}/document-label`, { documentLabelId });
}

// Where each document of a community comes from: its own design, the brand's or sonnnig's
async function getEnergyCommunityTemplates(energyCommunityId: string) {
    return get<ResolvedBrandTemplateDto[]>(`/v1/admin/energy-communities/${energyCommunityId}/templates`);
}

const mailPreviewBases: Record<MailPreviewScopeKind, string> = {
    Brand: '/v1/admin/brands',
    DocumentLabel: '/v1/admin/document-labels',
    EnergyCommunity: '/v1/admin/energy-communities'
};

// One mail of a brand, design or community, filled with sample values; nothing is sent
async function getMailPreview(scope: MailPreviewScope, type: DocumentTemplateType): Promise<MailPreviewDto> {
    return get<MailPreviewDto>(`${mailPreviewBases[scope.kind]}/${scope.id}/mail-previews/${type}`);
}

// Sends that preview as a test mail; a brand or community only, a design has no sender
async function sendTestMail(scope: MailPreviewScope, type: DocumentTemplateType, recipient: string): Promise<void> {
    const request: SendTestMailRequestDto = { recipient };
    await post<void>(`${mailPreviewBases[scope.kind]}/${scope.id}/mail-previews/${type}/test-mail`, request);
}

// Queues a sample invoice from the invoice template of a brand, design or community
async function startInvoicePreview(scope: MailPreviewScope): Promise<InvoicePreviewDto> {
    return post<InvoicePreviewDto>(`${mailPreviewBases[scope.kind]}/${scope.id}/invoice-previews`, {});
}

async function getInvoicePreview(id: string): Promise<InvoicePreviewDto> {
    return get<InvoicePreviewDto>(`/v1/admin/invoice-previews/${id}`);
}

// The rendered sample invoice, once its preview is done
async function getInvoicePreviewPdf(id: string): Promise<Blob> {
    try {
        const response = await authInstance.get(`/v1/admin/invoice-previews/${id}/pdf`, { responseType: 'blob' });
        return response.data as Blob;
    } catch (error: unknown) {
        // With responseType 'blob' the API's problem details arrive as a blob too; read them back so
        // apiErrorMessage finds the title.
        if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
            try {
                error.response.data = JSON.parse(await error.response.data.text());
            } catch {
                // Not JSON: keep the blob, the caller falls back to axios' own message.
            }
        }
        throw error;
    }
}

// Create a user with a placeholder account under the given brand; resolves to the new user id
async function createAdminUser(request: CreateAdminUserRequestDto) {
    return post<string>('/v1/Sonnnig/user', request);
}

// Permission management endpoints
async function createOrganizationMembership(data: {
    organizationId: string;
    accountId: string;
    teamId?: string | null;
    role: string;
}) {
    return post('/v1/Permission/organization/membership', data);
}

async function updateOrganizationMembership(membershipId: string, data: {
    teamId?: string | null;
    role?: string;
}) {
    return patch(`/v1/Permission/organization/membership/${membershipId}`, data);
}

async function createEnergyCommunityAdmin(data: {
    energyCommunityId: string;
    accountId: string;
    role: string;
}) {
    return post('/v1/Permission/energy-community/admin', data);
}

async function updateEnergyCommunityAdmin(adminId: string, data: {
    role?: string;
}) {
    return patch(`/v1/Permission/energy-community/admin/${adminId}`, data);
}

async function deleteEnergyCommunityAdmin(energyCommunityId: string, accountId: string) {
    return del(`/v1/Permission/energy-community/${energyCommunityId}/admin/${accountId}`);
}

async function inviteUser(data: {
    whitelabelId: string;
    name?: string;
    email: string;
    sendPasswordResetEmail?: boolean;
    roles?: string[] | null;
    organizationMemberships?: Array<{
        organizationId: string;
        teamId?: string | null;
        role: string;
    }>;
    energyCommunityAdmins?: Array<{
        energyCommunityId: string;
        role: string;
    }>;
}) {
    return post('/v1/Permission/invite', data);
}

async function bulkInviteUser(data: {
    whitelabelId: string;
    emails: string[];
    sendPasswordResetEmail?: boolean;
    roles?: string[] | null;
    organizationMemberships?: Array<{
        organizationId: string;
        teamId?: string | null;
        role: string;
    }>;
    energyCommunityAdmins?: Array<{
        energyCommunityId: string;
        role: string;
    }>;
    temporaryPassword?: string | null;
}) {
    return post('/v1/Permission/invite/bulk', data);
}

/** Deactivates an account: login disabled, API keys removed, everything else kept. */
async function deactivateAccount(accountId: string) {
    return post<DeactivateAccountResultDto>(`/v1/Permission/account/${accountId}/deactivate`);
}

/** Reactivates a deactivated account, optionally mailing a link to set a new password. */
async function reactivateAccount(accountId: string, sendPasswordEmail: boolean) {
    return post<ReactivateAccountResultDto>(`/v1/Permission/account/${accountId}/reactivate`, {sendPasswordEmail});
}

async function migrateUnboundUsers(data: {
    energyCommunityId?: string | null;
    limit?: number | null;
    dryRun?: boolean;
    temporaryPassword?: string | null;
    sendPasswordResetEmail?: boolean;
}) {
    return post('/v1/Permission/migrate-unbound', data);
}

async function resendPasswordReset(accountId: string) {
    return post(`/v1/Permission/account/${accountId}/resend-password-reset`);
}

/** Reads the account's Keycloak login identity: username, account email and verified state. */
async function getAccountLogin(accountId: string) {
    return get<AccountLoginDataDto>(`/v1/Permission/account/${accountId}/login`);
}

/** Changes only the account email and triggers a verification mail. The username stays as it is. */
async function updateAccountEmail(accountId: string, email: string) {
    return patch<AccountLoginUpdateResultDto>(`/v1/Permission/account/${accountId}/email`, { email });
}

/**
 * Sets a new login username *and* account email at once, then asks the user to verify the address
 * and pick a new password. The Keycloak user is updated in place, so permissions, memberships and
 * API keys survive.
 */
async function resetAccountLogin(accountId: string, userName: string, email: string) {
    return patch<AccountLoginUpdateResultDto>(`/v1/Permission/account/${accountId}/login`, { userName, email });
}

/** Reconciles the mirrored account username/email columns against Keycloak. Dry run by default. */
async function resyncAccountEmails(dryRun = true) {
    return post<{
        dryRun: boolean;
        totalAccounts: number;
        driftedCount: number;
        missingInKeycloakCount: number;
        entries: {
            accountId: string;
            storedUserName: string | null;
            keycloakUserName: string | null;
            storedEmail: string | null;
            keycloakEmail: string | null;
            missingInKeycloak: boolean;
        }[];
    }>(`/v1/Permission/accounts/resync-emails?dryRun=${dryRun}`);
}

async function getUnverifiedEmailAccounts(energyCommunityId?: string | null) {
    const query = energyCommunityId ? `?energyCommunityId=${encodeURIComponent(energyCommunityId)}` : '';
    return get<{
        accounts: {
            accountId: string,
            userName: string,
            email?: string | null,
            name?: string,
            subjectId: string,
            firstInvitedOn?: string | null,
            lastInvitedOn?: string | null,
            energyCommunities: string[],
            loginBlockedByCommunity: boolean,
            actionTokenLifespanSeconds: number
        }[],
        actionTokenLifespanSeconds: number
    }>(`/v1/Permission/accounts/unverified-emails${query}`);
}

async function bulkResendPasswordReset(accountIds: string[]) {
    return post<{successCount: number, failedCount: number, results: {accountId: string, success: boolean, error?: string}[]}>('/v1/Permission/accounts/bulk-resend-password-reset', { accountIds });
}

async function createApiKey(data: {
    email: string;
    name: string;
    claims: string[];
}) {
    return post<{
        id: string;
        accountId: string;
        name: string;
        key: string;
    }>('/v1/Permission/api-key', data);
}

async function resetDevDatabase(secretCode: string) {
    return post<{ message: string }>('/v1/DevTools/reset-database', { secretCode });
}

async function getWorkerStatus() {
    return get<{ isRunning: boolean; configured: boolean }>('/v1/DevTools/worker-status');
}

async function startWorker() {
    return post<{ message: string }>('/v1/DevTools/worker-start', {});
}

async function stopWorker() {
    return post<{ message: string }>('/v1/DevTools/worker-stop', {});
}

async function restartWorker() {
    return post<{ message: string }>('/v1/DevTools/worker-restart', {});
}

async function getDocumentCreatorStatus() {
    return get<{ isRunning: boolean; configured: boolean }>('/v1/invoice-runs/document-creator/status');
}

async function startDocumentCreator() {
    return post<{ message: string }>('/v1/invoice-runs/document-creator/start', {});
}

async function stopDocumentCreator() {
    return post<{ message: string }>('/v1/invoice-runs/document-creator/stop', {});
}

async function restartDocumentCreator() {
    return post<{ message: string }>('/v1/invoice-runs/document-creator/restart', {});
}

async function seedCommunity(data: {
    name: string;
    slug: string;
    startDate: string;
    userCount: number;
}) {
    return post<{ message: string }>('/v1/DevTools/seed-community', data);
}

// ---- Community metering points (/v1/communities/{id}/metering-points) ----

async function getCommunityMeteringPoints(
    energyCommunityId: string,
    query: CommunityMeteringPointQuery
): Promise<PagedResult<CommunityMeteringPointDto>> {
    const params: Record<string, string | number | boolean> = {
        page: query.page,
        pageSize: query.pageSize,
        activeOnly: query.activeOnly ?? false
    };
    if (query.search) params.search = query.search;

    return get<PagedResult<CommunityMeteringPointDto>>(`/v1/communities/${energyCommunityId}/metering-points`, { params });
}

// ---- Admin file management (/v1/admin/files) ----

async function getAdminFiles(query: FileListQuery): Promise<FileListResponseDto> {
    const params: Record<string, any> = {
        skip: query.skip ?? 0,
        top: query.top ?? 25
    };
    if (query.type) params.type = query.type;
    if (query.userId) params.userId = query.userId;
    if (query.energyCommunityId) params.energyCommunityId = query.energyCommunityId;
    if (query.search) params.search = query.search;
    if (query.periodStart) params.periodStart = query.periodStart;
    if (query.periodEnd) params.periodEnd = query.periodEnd;

    return get<FileListResponseDto>('/v1/admin/files', { params });
}

async function uploadAdminFile(payload: FileUploadPayload): Promise<FileDto> {
    const formData = new FormData();
    formData.append('file', payload.file);
    formData.append('name', payload.name);
    formData.append('type', payload.type);
    if (payload.userId) formData.append('userId', payload.userId);
    if (payload.energyCommunityId) formData.append('energyCommunityId', payload.energyCommunityId);
    if (payload.cloudBucketKey) formData.append('cloudBucketKey', payload.cloudBucketKey);
    if (payload.overwrite) formData.append('overwrite', 'true');
    if (payload.validFrom) formData.append('validFrom', payload.validFrom);
    if (payload.validTo) formData.append('validTo', payload.validTo);

    const response = await authInstance.post<FileDto>('/v1/admin/files', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
}

async function updateAdminFile(id: string, payload: FileUpdatePayload): Promise<FileDto> {
    return patch<FileDto>(`/v1/admin/files/${id}`, {
        name: payload.name,
        validFrom: payload.validFrom ?? null,
        validTo: payload.validTo ?? null,
        userId: payload.userId ?? null,
        energyCommunityId: payload.energyCommunityId ?? null
    });
}

async function deleteAdminFile(id: string) {
    return del(`/v1/admin/files/${id}`);
}

async function downloadAdminFile(id: string) {
    return authInstance.get(`/v1/admin/files/${id}/download`, {
        responseType: 'blob'
    });
}

async function bulkDownloadAdminFiles(request: BulkFileDownloadRequest) {
    return authInstance.post('/v1/admin/files/bulk-download', request, {
        responseType: 'blob'
    });
}

async function syncAdminBucketFiles(): Promise<{ created: number }> {
    return post<{ created: number }>('/v1/admin/files/sync-bucket', {});
}

// Server-side user search for owner/person pickers. Backed by the dedicated, paginated
// `/v1/users/search` endpoint (not OData): the backend does the filtering/paging and returns a
// pre-computed `displayName`. Without `energyCommunityId` it searches all users the caller may see;
// with it, the result is restricted to that community's active members.
async function searchUsers(searchTerm: string, energyCommunityId?: string): Promise<UserSearchResultDto[]> {
    const params: Record<string, string | number> = { page: 1, pageSize: 20 };
    const term = searchTerm?.trim();
    if (term) params.term = term;
    if (energyCommunityId) params.energyCommunityId = energyCommunityId;

    const response = await get<{ items: UserSearchResultDto[]; totalCount: number }>(
        '/v1/users/search',
        { params }
    );
    return response.items ?? [];
}

const sonnnig = {
    sendHabit,
    getHabits,
    communityDashboardData,
    dashboardData,
    identity,
    onboarding,
    publicOnboarding,
    getPublicOnboardingConfig,
    confirmSubmission,
    submitBankData,
    initialize,
    energyTimeSeries,
    energyDataAvailability,
    energyCommunityDataSummary,
    energyDataRequestOverview,
    canForecast,
    canNotify,
    tryGetEnergyCommunity,
    getEnergyCommunities,
    getEnergyCommunityWithMeteringPoints,
    getCommunityMeteringPoints,
    getMeteringPoints,
    getMeteringPointNumbers,
    resetUser,
    // EdaProcessViews functions
    getSystemEvents,
    getSystemEventTypes,
    getEdaConversations,
    getEdaConversation,
    closeEdaConversation,
    sendEdaMessage,
    sendPendingEdaMessages,
    reprocessConversations,
    reprocessMessages,
    // Test controller functions
    requestPodList,
    requestCommunityEnrollment,
    requestEnergyData,
    // Metering point user and premise management
    addMeteringPointPremise,
    updatePremise,
    addPremiseUser,
    updateUser,
    deleteMeteringPoint,
    deletePremise,
    exportCsv,
    exportTimeSeriesCsv,
    logAnalyticsEvent,
    processOnboardingSubmissions,
    odataGet,
    odataDelete,
    odataPatch,
    odataPost,
    // New Ponton community management endpoints
    requestCommunityWithdraw,
    changeParticipationFactor,
    requestMissingEnergyData,
    getUserRecommendation,
    getCommunityRecommendation,
    sendRecommendation,
    sendRecommendationCommunity,
    getWeeklyRecommendationRuns,
    forceSendWeeklyRecommendationRun,
    deleteWeeklyRecommendationRun,
    setWeeklyRecommendationRunState,
    checkBillability,
    getEnergyGraph,
    getEnergyTimeSeries,
    getInvoiceRunBreakdown,
    triggerBilling,
    queueInvoiceDocuments,
    getAdminInvoiceRuns,
    getCommunityTariffs,
    createFixedTariff,
    updateFixedTariff,
    setFixedTariffEndDate,
    deleteFixedTariff,
    createFlatRateTariff,
    updateFlatRateTariff,
    setFlatRateTariffEndDate,
    deleteFlatRateTariff,
    getOnboardingForm,
    saveOnboardingForm,
    getUserTransactions,
    createUserTransaction,
    updateUserTransaction,
    deleteUserTransaction,
    searchUsers,
    recheckInvoiceRun,
    forceAdvanceInvoiceRun,
    recalculateInvoiceRun,
    getInvoiceRunComparison,
    confirmInvoiceRun,
    deleteInvoiceRun,
    setInvoiceRunVisibility,
    getAdminInvoiceRunOverview,
    getInvoiceRunTodos,
    migrateMembershipToUser,
    getWhitelabels,
    getBrands,
    getBrand,
    getBrandCommunities,
    getBrandAccounts,
    getBrandThemes,
    getBrandSuggestion,
    getBrandDocumentLabels,
    createBrand,
    updateBrand,
    deleteBrand,
    checkBrandSetup,
    createBrandWithDesign,
    getBrandCommunityDesigns,
    getDocumentLabels,
    getDocumentLabel,
    createDocumentLabel,
    renameDocumentLabel,
    deleteDocumentLabel,
    validateBrandTemplate,
    uploadBrandTemplate,
    removeBrandTemplate,
    setEnergyCommunityDocumentLabel,
    getEnergyCommunityTemplates,
    getMailPreview,
    sendTestMail,
    startInvoicePreview,
    getInvoicePreview,
    getInvoicePreviewPdf,
    createAdminUser,
    // Permission management
    createOrganizationMembership,
    updateOrganizationMembership,
    createEnergyCommunityAdmin,
    updateEnergyCommunityAdmin,
    deleteEnergyCommunityAdmin,
    inviteUser,
    bulkInviteUser,
    deactivateAccount,
    reactivateAccount,
    migrateUnboundUsers,
    resendPasswordReset,
    getAccountLogin,
    updateAccountEmail,
    resetAccountLogin,
    resyncAccountEmails,
    getUnverifiedEmailAccounts,
    bulkResendPasswordReset,
    createApiKey,
    // DevTools
    resetDevDatabase,
    getWorkerStatus,
    startWorker,
    stopWorker,
    restartWorker,
    getDocumentCreatorStatus,
    startDocumentCreator,
    stopDocumentCreator,
    restartDocumentCreator,
    seedCommunity,
    // Admin file management
    getAdminFiles,
    uploadAdminFile,
    updateAdminFile,
    deleteAdminFile,
    downloadAdminFile,
    bulkDownloadAdminFiles,
    syncAdminBucketFiles
};

export default sonnnig;
